// ==================== CUSTOMERS SERVICE ====================

import { apiService } from './api';
import type { ApiResponse, PaginatedResponse } from '../types';
import type { LeadGenAddonQuote, OrganizationsQuote, PriceCalculation } from './subscription.service';

// ==================== TYPES ====================

export interface CustomerUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

export interface CustomerSubscription {
  id: string;
  status: string;
  billingCycle?: 'MONTHLY' | 'YEARLY';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  autoRenew?: boolean;
  nextRenewalAmount?: number;
  currency?: string;
  stripeCustomerId?: string | null;
  defaultPaymentMethodId?: string | null;
  leadGenAddonEnabled?: boolean;
  /** Multiple organizations: organizations purchased */
  organizationCount?: number;
  plan: {
    id: string;
    name: string;
    leadGenAddonFee?: number | string;
  };
}

/** Multiple organizations: an extra organization of a customer (admin details) */
export interface CustomerChildOrganization {
  id: string;
  businessName: string;
  businessEmail?: string;
  businessPhone?: string;
  createdAt: string;
  whatsappAccount?: {
    id: string;
    displayPhoneNumber?: string | null;
    status: string;
  } | null;
  _count?: {
    contacts: number;
    campaigns: number;
    templates: number;
    messages?: number;
  };
}

export interface CustomerOrganization {
  id: string;
  businessName: string;
  businessEmail?: string;
  businessPhone?: string;
  businessAddress?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  user: CustomerUser;
  subscription?: CustomerSubscription;
  _count?: {
    contacts: number;
    campaigns: number;
    templates: number;
    messages?: number;
  };
  /** Customer details only: the customer's WhatsApp number */
  whatsappAccount?: CustomerChildOrganization['whatsappAccount'];
  /** Customer details only, multiple organizations: the extra organizations */
  childOrganizations?: CustomerChildOrganization[];
  currentCoupon?: { code: string; resellerName: string } | null;
  nextRenewalAmount?: number;
}

export interface CustomerStatistics {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  suspended: number;
}

export interface CustomerListQuery {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';
}

export interface UpdateStatusDto {
  status: 'APPROVED' | 'REJECTED' | 'SUSPENDED';
  reason?: string;
}

export interface AssignPlanData {
  planId: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  paymentMode: 'CASH' | 'CHECK' | 'BANK_TRANSFER' | 'OTHER';
  paymentReference?: string;
  notes?: string;
  includeSetupFee?: boolean;
  includeLeadGenAddon?: boolean;
  /** Multiple organizations: plan price and setup fee are charged per organization */
  organizationCount?: number;
  couponCode?: string;
}

export interface ExpireSubscriptionData {
  /** Stored on the subscription as cancelReason for audit. */
  reason?: string;
}

export interface LeadGenAddonData {
  enabled: boolean;
  /** Required when enabling, unless the add-on is already paid for this period. */
  paymentMode?: AssignPlanData['paymentMode'];
  paymentReference?: string;
  notes?: string;
}

/** Multiple organizations: organizations added for a customer paying offline */
export interface AddOrganizationsData {
  count: number;
  includeSetupFee?: boolean;
  paymentMode: AssignPlanData['paymentMode'];
  paymentReference?: string;
  notes?: string;
}

// ==================== SERVICE ====================

export const customersService = {
  /**
   * Get all customers with pagination
   */
  getAll: async (query?: CustomerListQuery): Promise<ApiResponse<PaginatedResponse<CustomerOrganization>>> => {
    const params = new URLSearchParams();
    if (query?.page) params.append('page', query.page.toString());
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.sortBy) params.append('sortBy', query.sortBy);
    if (query?.sortOrder) params.append('sortOrder', query.sortOrder);
    if (query?.search) params.append('search', query.search);
    if (query?.status) params.append('status', query.status);
    
    const queryString = params.toString();
    const url = `/admin/customers${queryString ? `?${queryString}` : ''}`;
    
    return apiService.get<PaginatedResponse<CustomerOrganization>>(url);
  },

  /**
   * Get customer statistics
   */
  getStatistics: async (): Promise<ApiResponse<CustomerStatistics>> => {
    return apiService.get<CustomerStatistics>('/admin/customers/statistics');
  },

  /**
   * Get customer by ID
   */
  getById: async (id: string): Promise<ApiResponse<CustomerOrganization>> => {
    return apiService.get<CustomerOrganization>(`/admin/customers/${id}`);
  },

  /**
   * Approve customer
   */
  approve: async (id: string): Promise<ApiResponse<CustomerOrganization>> => {
    return apiService.patch<CustomerOrganization>(`/admin/customers/${id}/approve`);
  },

  /**
   * Update customer status (approve, reject, suspend)
   */
  updateStatus: async (id: string, data: UpdateStatusDto): Promise<ApiResponse<CustomerOrganization>> => {
    return apiService.patch<CustomerOrganization>(`/admin/customers/${id}/status`, data);
  },

  /**
   * Reactivate suspended customer
   */
  reactivate: async (id: string): Promise<ApiResponse<CustomerOrganization>> => {
    return apiService.patch<CustomerOrganization>(`/admin/customers/${id}/reactivate`);
  },

  /**
   * What Assign Plan would record (list amounts, coupon discount, next renewal)
   */
  getAssignPlanQuote: async (
    id: string,
    data: Pick<AssignPlanData, 'planId' | 'billingCycle' | 'includeSetupFee' | 'includeLeadGenAddon' | 'organizationCount' | 'couponCode'>,
  ): Promise<ApiResponse<PriceCalculation>> => {
    const params = new URLSearchParams({ planId: data.planId, billingCycle: data.billingCycle });
    if (data.includeSetupFee) params.set('includeSetupFee', 'true');
    if (data.includeLeadGenAddon) params.set('includeLeadGenAddon', 'true');
    if (data.organizationCount) params.set('organizationCount', String(data.organizationCount));
    if (data.couponCode) params.set('couponCode', data.couponCode);
    return apiService.get<PriceCalculation>(`/admin/customers/${id}/assign-plan/quote?${params}`);
  },

  /**
   * Assign subscription plan manually (offline payment)
   */
  assignPlan: async (id: string, data: AssignPlanData): Promise<ApiResponse<CustomerOrganization>> => {
    return apiService.post<CustomerOrganization>(`/admin/customers/${id}/assign-plan`, data);
  },

  /**
   * Expire a customer's plan and stop their WhatsApp / SMS services.
   * Undone by assigning a plan again, or when the customer subscribes themselves.
   */
  expireSubscription: async (
    id: string,
    data?: ExpireSubscriptionData,
  ): Promise<ApiResponse<CustomerOrganization>> => {
    return apiService.patch<CustomerOrganization>(
      `/admin/customers/${id}/expire-subscription`,
      data ?? {},
    );
  },

  /**
   * What enabling the Lead Generation add-on would record for this customer now.
   */
  getLeadGenAddonQuote: async (id: string): Promise<ApiResponse<LeadGenAddonQuote>> => {
    return apiService.get<LeadGenAddonQuote>(`/admin/customers/${id}/lead-gen-addon/quote`);
  },

  /**
   * Enable or disable the Lead Generation add-on for a customer paying offline.
   * Enabling records the add-on fee as an offline payment.
   */
  setLeadGenAddon: async (id: string, data: LeadGenAddonData): Promise<ApiResponse<CustomerOrganization>> => {
    return apiService.patch<CustomerOrganization>(`/admin/customers/${id}/lead-gen-addon`, data);
  },

  /**
   * What adding organizations would record for this customer now.
   */
  getOrganizationsQuote: async (
    id: string,
    count: number,
    includeSetupFee: boolean,
  ): Promise<ApiResponse<OrganizationsQuote>> => {
    const params = new URLSearchParams({
      count: String(count),
      includeSetupFee: String(includeSetupFee),
    });
    return apiService.get<OrganizationsQuote>(`/admin/customers/${id}/organizations/quote?${params}`);
  },

  /**
   * Add organizations for a customer paying offline. Records the plan price
   * (and setup fee, if chosen) of each one as an offline payment.
   */
  addOrganizations: async (id: string, data: AddOrganizationsData): Promise<ApiResponse<CustomerOrganization>> => {
    return apiService.post<CustomerOrganization>(`/admin/customers/${id}/organizations`, data);
  },
};

export default customersService;

